import type { ConversationTurn, DraftRequest, PipelineStage } from '../types';
import { draftText } from '../draft-text';

const FILE_PATH_RE = /\b[\w./-]+\.[A-Za-z]{1,10}\b/g;
const MAX_POINT_LENGTH = 140;

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s;
}

/** Local, non-LLM heuristic: pulls out file paths mentioned and the gist of each user turn. */
function heuristicSummaryFor(turns: ConversationTurn[]): string {
  const filePaths = new Set<string>();
  const points: string[] = [];
  for (const turn of turns) {
    for (const m of turn.content.match(FILE_PATH_RE) ?? []) filePaths.add(m);
    if (turn.role === 'user') {
      const firstLine = turn.content.split('\n').find((l) => l.trim().length > 0)?.trim();
      if (firstLine) points.push(truncate(firstLine, MAX_POINT_LENGTH));
    }
  }
  const lines: string[] = [];
  if (points.length > 0) lines.push(`Earlier in this conversation: ${points.join(' — ')}`);
  if (filePaths.size > 0) lines.push(`Files referenced: ${Array.from(filePaths).join(', ')}`);
  return (
    lines.join('\n') || `${turns.length} earlier turn(s) omitted (no file references or user requests found by the heuristic summarizer).`
  );
}

function appendSummary(existing: string | undefined, addition: string): string {
  return existing ? `${existing}\n${addition}` : addition;
}

function buildSummarizationPrompt(existingSummary: string | undefined, newTurns: ConversationTurn[]): string {
  const turnsText = newTurns.map((t) => `${t.role}: ${t.content}`).join('\n\n');
  const instruction =
    'Summarize this conversation excerpt concisely for reuse as background context in a later turn. ' +
    'Focus on file names discussed, decisions made, and open tasks. Plain prose, no preamble, no markdown headers.';
  return existingSummary
    ? `Existing summary of earlier turns:\n${existingSummary}\n\n${instruction} Fold in these new turns:\n\n${turnsText}`
    : `${instruction}\n\n${turnsText}`;
}

export const d1RollingHistorySummarization: PipelineStage = {
  id: 'D1',
  group: 'D',
  label: 'Rolling history summarization',
  description: 'Keep recent turns verbatim; summarize older history once it exceeds a token threshold.',
  impact: 'Medium',
  defaultEnabled: true,
  implemented: true,
  params: {
    verbatimTurns: { type: 'number', default: 6, min: 0, max: 50, label: 'Verbatim turns to keep (N)' },
    tokenThreshold: { type: 'number', default: 8000, min: 500, max: 100000, label: 'Summarize above (tokens)' },
    llmAssistedSummarization: { type: 'boolean', default: false, label: 'LLM-assisted summarization' },
  },

  async run(draft, params, ctx) {
    const tokensBefore = ctx.estimateTokens(draftText(draft));
    const verbatimTurns = Math.max(0, Number(params.verbatimTurns) || 0);
    const tokenThreshold = Number(params.tokenThreshold) || 8000;

    const historyTokens = ctx.estimateTokens(draft.history.map((t) => t.content).join('\n'));
    if (draft.history.length <= verbatimTurns || historyTokens <= tokenThreshold) {
      return {
        draft,
        report: {
          stageId: 'D1',
          tokensBefore,
          tokensAfter: tokensBefore,
          tokensSaved: 0,
          notes: [
            draft.history.length <= verbatimTurns
              ? 'History is already within the verbatim window — nothing to summarize.'
              : `History (~${historyTokens} tokens) is under the ${tokenThreshold}-token threshold — nothing to summarize yet.`,
          ],
        },
      };
    }

    const splitIndex = draft.history.length - verbatimTurns;
    const older = draft.history.slice(0, splitIndex);
    const recent = draft.history.slice(splitIndex);

    // How many of `older` a *previous* LLM-assisted round already folded into draft.historySummary
    // (persisted by the caller) — lets us ask the model to fold in only the new turns instead of
    // re-summarizing everything from scratch every message.
    const alreadyCovered = Math.min(draft.historySummarizedThroughCount ?? 0, older.length);
    const newOlderTurns = older.slice(alreadyCovered);

    const notes = [
      `Folded ${older.length} older turn(s) into a rolling summary, kept the last ${recent.length} turn(s) verbatim.`,
    ];
    let combinedSummary: string;
    let historySummarizedThroughCountThisRound: number | undefined;

    if (params.llmAssistedSummarization && ctx.summarizeWithModel) {
      if (newOlderTurns.length === 0 && draft.historySummary) {
        combinedSummary = draft.historySummary;
        historySummarizedThroughCountThisRound = older.length;
        notes.push('Reused the existing LLM-generated summary — no new turns to fold in since last time.');
      } else {
        try {
          const prompt = buildSummarizationPrompt(alreadyCovered > 0 ? draft.historySummary : undefined, newOlderTurns);
          const result = await ctx.summarizeWithModel(prompt, ctx.signal);
          combinedSummary = result.trim();
          historySummarizedThroughCountThisRound = older.length;
          notes.push(
            `Folded ${newOlderTurns.length} new older turn(s) into an LLM-generated summary (${older.length} turn(s) covered in total).`,
          );
        } catch (err) {
          // Fail open: a transient network/API failure falls back to the heuristic for just the
          // newly-folded turns, and doesn't touch the caller's persisted LLM summary/count — the
          // next message will simply retry the LLM path from where it last succeeded.
          combinedSummary = appendSummary(draft.historySummary, heuristicSummaryFor(newOlderTurns));
          notes.push(
            `LLM-assisted summarization failed (${err instanceof Error ? err.message : String(err)}) — used the local heuristic for the newly-folded turns instead.`,
          );
        }
      }
    } else {
      // Heuristic-only mode is stateless by design (cheap enough to redo in full each time), so it
      // never relies on — or persists — historySummarizedThroughCount.
      combinedSummary = heuristicSummaryFor(older);
      if (params.llmAssistedSummarization) {
        notes.push(
          'LLM-assisted summarization is on, but no model hook is available (e.g. no API key set) — used the local heuristic instead.',
        );
      }
    }

    const newDraft: DraftRequest = {
      ...draft,
      history: recent,
      historySummary: combinedSummary,
      historySummarizedThroughCountThisRound,
    };
    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'D1',
        tokensBefore,
        tokensAfter,
        tokensSaved: Math.max(0, tokensBefore - tokensAfter),
        notes,
      },
    };
  },
};
